/**
 * Writer-facing Nudge card words.
 * TODO-Aoide: placeholders. Aoide drafts them, Joel approves them.
 * Every string a writer can see or hear on these cards lives in this file.
 */

export const NUDGE_LIST_LABEL = 'Ideas from Brainstormy';
export const NUDGE_SAVE = 'Save';
export const NUDGE_SAVED = 'Saved';
export const NUDGE_SAVE_LABEL = 'Save this idea';
export const NUDGE_DISMISS_LABEL = 'Dismiss this idea';
export const NUDGE_UNDO = 'Undo';
export const NUDGE_UNDO_LABEL = 'Undo dismiss';
export const NUDGE_IDEA_FALLBACK = 'Idea';

// Approved Nov 1 (Joel). #2683 menu, posted line, new chat, and the Saved ask.
export const NUDGE_MORE_LABEL = 'More for this idea';
export const NUDGE_GO_NOW = 'Go with this now';
export const NUDGE_OPEN_NEW = 'Open in a new chat';
export const NUDGE_SAVED_TOAST = 'Saved to Saved ideas.';
export const NUDGE_NEW_CHAT_FAILED = "Couldn't open a new chat. Your idea is saved.";
export const NUDGE_REMOVE_ASK = 'Remove it from Saved ideas too?';
export const NUDGE_REMOVE = 'Remove';
export const NUDGE_KEEP_SAVED = 'Keep it saved';
export const NUDGE_DISMISSED_LINE = "Dismissed. Brainstormy won't build on it. Undo";

export function goWithMessage(headline) {
  return `Let's go with this: ${headline}`;
}

export function fromIdeaNote(chatName) {
  return `From an idea in ${chatName}`;
}

export function openedInNote(chatName) {
  return `Opened in ${chatName}`;
}

export function fromChatNote(chatName) {
  return `From ${chatName}`;
}

export function dismissedStub(headline) {
  return `Dismissed: ${headline}`;
}

/** Lead text plus the chat name, so the name can be the link. */
export function noteParts(note, chatName) {
  const name = String(chatName || '');
  const text = String(note || '');
  if (!name || !text.endsWith(name)) return null;
  return { lead: text.slice(0, text.length - name.length), name };
}

export function splitSeedNote(content, chatName) {
  const note = fromIdeaNote(chatName);
  const text = String(content || '');
  if (!text.endsWith(note)) return null;
  const parts = noteParts(note, chatName);
  if (!parts) return null;
  return {
    idea: text.slice(0, text.length - note.length).replace(/\s+$/, ''),
    ...parts,
  };
}

/** Emphasis marks are not part of a title the writer reads as plain text. */
export function stripMarkdown(text) {
  return String(text || '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/gs, '$2')
    .replace(/(?<!\w)(\*|_)(?!\s)(.+?)(?<!\s)\1(?!\w)/gs, '$2')
    .trim();
}

/** Headline with emphasis marks removed, for a chat name or a posted line. */
export function plainHeadline(text) {
  return stripMarkdown(ideaHeadline(text));
}

/** Drop Saved on a card whose Idea was deleted. Dismissed stays dismissed. */
export function messagesWithoutDeletedSave(messages, captureId) {
  if (!captureId) return messages;
  const id = String(captureId);
  return (messages || []).map((msg) => {
    const items = msg?.metadata?.items;
    if (!Array.isArray(items)) return msg;
    let changed = false;
    const nextItems = items.map((item) => {
      if (String(item?.capture_id || '') !== id) return item;
      if (actionForVote(item.vote) !== 'saved') return item;
      changed = true;
      const next = { ...item, vote: null };
      delete next.capture_id;
      return next;
    });
    if (!changed) return msg;
    return { ...msg, metadata: { ...msg.metadata, items: nextItems } };
  });
}

/** Same label ChatPaneFrame uses when a chat has no name. */
const UNTITLED_CHAT = 'Untitled chat';

/** Same line the account screen already shows. */
const TRY_AGAIN = 'Something went wrong. Please try again.';

export function seedChatText(ideaText, chatName) {
  const idea = stripMarkdown(ideaText);
  const name = String(chatName || '').trim() || UNTITLED_CHAT;
  const note = fromIdeaNote(name);
  return idea ? `${idea}\n\n${note}` : note;
}

export function captureIdFrom(updated, itemId) {
  const meta = updated?.metadata || updated?.data?.metadata;
  const found = (meta?.items || []).find((item) => String(item.id) === String(itemId));
  return found?.capture_id || null;
}

/**
 * Post the approved line. Save only after the line stays in the chat.
 * A credits failure removes the line, so the card is left as it was.
 */
export async function runGoWithIdea({ item, send, save, toast, paused, onPaused }) {
  if (paused) {
    onPaused?.();
    return;
  }
  let posted = true;
  await send(goWithMessage(plainHeadline(item?.text)), {
    onFailed: () => { posted = false; },
  });
  if (!posted) return;
  try {
    await save();
  } catch {
    toast(TRY_AGAIN);
    return;
  }
  toast(NUDGE_SAVED_TOAST);
}

/**
 * Save, then open a new chat seeded with the idea. On failure the idea stays saved.
 */
export async function runOpenInNewChat({
  item,
  chatName,
  sourceSessionId,
  save,
  spawn,
  markOpened,
  toast,
  openSession,
}) {
  let updated;
  try {
    updated = await save();
  } catch {
    toast(TRY_AGAIN);
    return;
  }
  const captureId = captureIdFrom(updated, item?.id);
  if (!captureId) {
    toast(TRY_AGAIN);
    return;
  }
  const name = String(chatName || '').trim() || UNTITLED_CHAT;
  try {
    const result = await spawn(captureId, {
      session_name: plainHeadline(item?.text),
      guidance_mode: 'explore',
      seed_text: seedChatText(item?.text, name),
      source_session_id: sourceSessionId || null,
      source_session_name: name,
    });
    const session = result?.data?.session || result?.session;
    if (!session?.id) throw new Error('no session');
    await markOpened(session.id, session.name || plainHeadline(item?.text));
    toast(NUDGE_SAVED_TOAST);
    openSession(session.id);
  } catch {
    toast(NUDGE_NEW_CHAT_FAILED);
  }
}

export const UNDO_MS = 6000;

/** Headline length, including the ellipsis when a sentence runs long. */
const HEADLINE_CAP = 90;
const TITLE_PERIOD = /^(?:Dr|Mr|Mrs|St)$/i;

export function actionForVote(vote) {
  if (vote === 'up' || vote === 'saved' || vote === 'keep') return 'saved';
  if (vote === 'down' || vote === 'dismissed' || vote === 'skip') return 'dismissed';
  return null;
}

export function voteForAction(action) {
  if (action === 'saved') return 'up';
  if (action === 'dismissed') return 'down';
  return null;
}

function firstLine(raw) {
  const lineBreak = raw.search(/\r?\n/);
  return lineBreak < 0 ? raw : raw.slice(0, lineBreak);
}

function isTitleOrDecimalPeriod(text, index) {
  const before = text[index - 1] || '';
  const after = text[index + 1] || '';
  if (/\d/.test(before) && /\d/.test(after)) return true;
  const word = (text.slice(0, index).match(/(\S+)$/) || [])[1] || '';
  return TITLE_PERIOD.test(word);
}

function sentenceEndIndex(text) {
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch !== '.' && ch !== '!' && ch !== '?' && ch !== '\u2026') continue;
    if (ch === '.' && isTitleOrDecimalPeriod(text, i)) continue;
    const next = text[i + 1];
    if (next !== undefined && !/\s/.test(next)) continue;
    return i;
  }
  return -1;
}

function capAtWord(text) {
  if (text.length <= HEADLINE_CAP) {
    return { display: text, consumed: text.length };
  }
  const room = HEADLINE_CAP - 1;
  const breakAt = text.lastIndexOf(' ', room);
  if (breakAt <= 0) {
    const head = text.slice(0, room).trimEnd();
    return { display: `${head}\u2026`, consumed: head.length };
  }
  const head = text.slice(0, breakAt).trimEnd();
  return { display: `${head}\u2026`, consumed: breakAt };
}

function headlineSpan(raw) {
  const line = firstLine(raw);
  const end = sentenceEndIndex(line);
  const candidate = end < 0 ? line : line.slice(0, end + 1);
  const capped = capAtWord(candidate);
  return { display: capped.display, sourceEnd: capped.consumed };
}

/** The idea's own first sentence, markdown kept so emphasis still renders. */
export function ideaHeadline(text) {
  const raw = String(text || '').trim();
  if (!raw) return NUDGE_IDEA_FALLBACK;
  return headlineSpan(raw).display || NUDGE_IDEA_FALLBACK;
}

/** Remainder after the headline. Empty when the headline is the whole idea. */
export function ideaBody(text) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  const { sourceEnd } = headlineSpan(raw);
  if (sourceEnd >= raw.length) return '';
  return raw.slice(sourceEnd).trim();
}
